
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CalendarConnection, CalendarSelection } from "@/types/calendar";

export const useCalendarConnections = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [calendarConnections, setCalendarConnections] = useState<CalendarConnection[]>([]);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  useEffect(() => {
    async function checkCalendarConnections() {
      if (!currentUser) {
        setIsCheckingConnection(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('calendar_connections')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error("Error checking calendar connections:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to check calendar connection status",
          });
        }

        if (data && data.length > 0) {
          const connections = data.map(rawConnection => {
            let selectedCalendars: CalendarSelection[] = [];
            
            console.log("Raw selected_calendars from DB:", rawConnection.selected_calendars);
            
            if (rawConnection.selected_calendars && Array.isArray(rawConnection.selected_calendars)) {
              selectedCalendars = rawConnection.selected_calendars.map(cal => {
                if (typeof cal === 'string') {
                  try {
                    const parsed = JSON.parse(cal);
                    return { 
                      id: parsed.id || '', 
                      name: parsed.name || '',
                      isPrimary: parsed.isPrimary || false
                    };
                  } catch (e) {
                    return { 
                      id: cal, 
                      name: cal, 
                      isPrimary: false
                    };
                  }
                } else if (typeof cal === 'object' && cal !== null) {
                  const calObj = cal as any;
                  return { 
                    id: calObj.id || '',
                    name: calObj.name || calObj.id || '',
                    isPrimary: calObj.isPrimary || false
                  };
                }
                return { id: '', name: '', isPrimary: false };
              }).filter(cal => cal.id !== '');
            }
            
            console.log("Final reconstructed selected calendars:", selectedCalendars);
            
            // Find the primary calendar ID from the reconstructed calendars
            const primaryCalendar = selectedCalendars.find(cal => cal.isPrimary);
            const primaryCalendarId = primaryCalendar ? primaryCalendar.id : undefined;
            
            return {
              id: rawConnection.id,
              user_id: rawConnection.user_id,
              provider: rawConnection.provider,
              access_token: rawConnection.access_token,
              refresh_token: rawConnection.refresh_token,
              token_type: rawConnection.token_type,
              expires_at: rawConnection.expires_at,
              created_at: rawConnection.created_at,
              updated_at: rawConnection.updated_at,
              selected_calendars: selectedCalendars,
              account_email: rawConnection.account_email || '',
              account_name: rawConnection.account_name || '',
              is_primary: (rawConnection as any).is_primary || false,
              primary_calendar_id: primaryCalendarId
            };
          });
          
          setCalendarConnections(connections);
          
          // Check if user has multiple providers but no primary set
          if (connections.length > 1 && !connections.some(conn => conn.is_primary)) {
            await checkAndShowPrimaryProviderDialog(connections);
          }
        } else {
          setCalendarConnections([]);
        }
        
      } catch (error) {
        console.error("Error:", error);
        setCalendarConnections([]);
      } finally {
        setIsCheckingConnection(false);
      }
    }

    checkCalendarConnections();
  }, [currentUser, toast]);

  const checkAndShowPrimaryProviderDialog = async (connections: CalendarConnection[]) => {
    if (!currentUser) return;

    try {
      // Check if there's a recent connection (within last 5 minutes)
      const recentConnection = connections.find(conn => {
        const createdAt = new Date(conn.created_at);
        const now = new Date();
        const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
        return diffMinutes < 5;
      });

      // Only show dialog if there's a recent connection
      if (recentConnection) {
        setTimeout(() => {
          toast({
            title: "Multiple Calendars Connected",
            description: "You can now set a primary calendar in the calendar settings below.",
          });
        }, 1000);
      }
    } catch (error) {
      console.error("Error checking primary provider:", error);
    }
  };

  const handleCalendarConnectionUpdate = (updatedConnection: CalendarConnection) => {
    console.log("Updating calendar connection:", updatedConnection);
    setCalendarConnections(connections => 
      connections.map(conn => 
        conn.id === updatedConnection.id ? updatedConnection : conn
      )
    );
  };

  const removeCalendarConnection = (connectionId: string) => {
    console.log("Removing calendar connection:", connectionId);
    setCalendarConnections(connections => 
      connections.filter(conn => conn.id !== connectionId)
    );
  };

  return {
    calendarConnections,
    isCheckingConnection,
    handleCalendarConnectionUpdate,
    removeCalendarConnection
  };
};
